import React from 'react';
import { createStyles, Grid, makeStyles, MenuItem, TextField } from '@material-ui/core';
import { useTranslation } from '@fluentsms/i18n';
import { Filter } from '../DataGridFilterMenu';

export type DataGridNumberFilterPropTypes = {
  filter: Filter;
  onFilterChange: (filterValue: Filter) => void;
};

// DataGridFilterMenu styles, nothing more
const useStyles = makeStyles(() =>
  createStyles({
    columnInputLabel: {
      textTransform: 'capitalize',
    },
  }),
);

export const DataGridNumberFilter = ({ filter, onFilterChange }: DataGridNumberFilterPropTypes): JSX.Element => {
  const classes = useStyles();
  // coercing to 'any' because i18n-next t-function is causing typescript
  // errors even when used correctly
  const { t }: { t: any } = useTranslation('common'); // eslint-disable-line @typescript-eslint/no-explicit-any
  const handleValueChange = (
    e: React.ChangeEvent<{
      name?: string | undefined;
      value: unknown;
    }>,
  ) => {
    onFilterChange({ ...filter, value: (e.target.value as number) || '' });
  };

  const handleOperatorChange = (
    e: React.ChangeEvent<{
      name?: string | undefined;
      value: unknown;
    }>,
  ) => {
    onFilterChange({ ...filter, operator: (e.target.value as string) || '' });
  };

  const isEmptyOrNotEmptyFilter = filter.operator === 'isEmpty' || filter.operator === 'isNotEmpty';

  return (
    <Grid container>
      {/* Render <select> menu of all available table columns */}
      <Grid item xs={12} sm={6}>
        <TextField
          id="operator-field"
          value={filter.operator || ''}
          label={t('operator')}
          InputLabelProps={{
            className: classes.columnInputLabel,
          }}
          onChange={handleOperatorChange}
          data-testid="data-grid-number-filter-operator-field"
          select
          fullWidth
        >
          <MenuItem
            key={`filter-operator-equals`}
            value="equals"
            data-testid="data-grid-string-filter-operator-option-equals"
          >
            equals
          </MenuItem>
          <MenuItem
            key={`filter-operator-not-equals`}
            value="notEquals"
            data-testid="data-grid-string-filter-operator-option-not-equals"
          >
            not equals
          </MenuItem>
          <MenuItem value="isEmpty" data-testid="data-grid-string-filter-operator-option-is-empty">
            is empty
          </MenuItem>
          <MenuItem value="isNotEmpty" data-testid="data-grid-string-filter-operator-option-is-not-empty">
            is not empty
          </MenuItem>
          <MenuItem
            key={`filter-operator-greater-than`}
            value="greaterThan"
            data-testid="data-grid-string-filter-operator-option-greater-than"
          >
            greater than
          </MenuItem>
          <MenuItem
            key={`filter-operator-greater-than-or-equal`}
            value="greaterThanOrEqual"
            data-testid="data-grid-string-filter-operator-option-greater-than-or-equal"
          >
            greater than or equal
          </MenuItem>
          <MenuItem
            key={`filter-operator-less-than`}
            value="lessThan"
            data-testid="data-grid-string-filter-operator-option-less-than"
          >
            less than
          </MenuItem>
          <MenuItem
            key={`filter-operator-less-than-or-equal`}
            value="lessThanOrEqual"
            data-testid="data-grid-string-filter-operator-option-less-than-or-equal"
          >
            less than or equal
          </MenuItem>
        </TextField>
      </Grid>
      {/* Render the filter associated with that column */}
      <Grid item xs={12} sm={6}>
        <TextField
          disabled={isEmptyOrNotEmptyFilter}
          label={t('value')}
          value={isEmptyOrNotEmptyFilter ? '' : filter.value || ''}
          InputLabelProps={{
            className: classes.columnInputLabel,
          }}
          onChange={handleValueChange}
          data-testid="data-grid-number-filter-value-field"
          fullWidth
        />
      </Grid>
    </Grid>
  );
};
